'use client';

import { useEffect, useState } from 'react';

let sharedContentFilterRef: HTMLElement | null = null;
let sharedStickyHeaderRef: HTMLElement | null = null;
const listeners: Set<(isVisible: boolean) => void> = new Set();
let isObserverAttached = false;
let currentObserver: IntersectionObserver | null = null;

const calculateOptimalMargin = (): string => {
  if (!sharedStickyHeaderRef) {
    return '0px 0px 0px 0px';
  }

  const stickyHeaderHeight = sharedStickyHeaderRef.offsetHeight;

  const margin = Math.max(stickyHeaderHeight - 10, 1);

  return `-${margin}px 0px 0px 0px`;
};

const setupObserver = () => {
  if (currentObserver) {
    currentObserver.disconnect();
    currentObserver = null;
    isObserverAttached = false;
  }

  if (!sharedContentFilterRef || !sharedStickyHeaderRef) return;

  const optimalMargin = calculateOptimalMargin();

  currentObserver = new IntersectionObserver(
    ([entry]) => {
      const newValue = !entry.isIntersecting;
      listeners.forEach((listener) => listener(newValue));
    },
    { threshold: 0, rootMargin: optimalMargin }
  );

  currentObserver.observe(sharedContentFilterRef);
  isObserverAttached = true;
};

/**
 * Хук для синхронизации видимости StickyHeader в зависимости от позиции FilterPanel
 */
export const useFilterSync = () => {
  const [isStickyVisible, setIsStickyVisible] = useState(false);

  useEffect(() => {
    listeners.add(setIsStickyVisible);

    const initTimer = setTimeout(() => {
      if (
        !isObserverAttached &&
        sharedContentFilterRef &&
        sharedStickyHeaderRef
      ) {
        setupObserver();
      }
    }, 100);

    const handleResize = () => {
      setupObserver();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initTimer);
      listeners.delete(setIsStickyVisible);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const setContentFilterRef = (element: HTMLElement | null) => {
    if (element !== sharedContentFilterRef) {
      sharedContentFilterRef = element;
      setTimeout(setupObserver, 0);
    }
  };

  const setStickyHeaderRef = (element: HTMLElement | null) => {
    if (element !== sharedStickyHeaderRef) {
      sharedStickyHeaderRef = element;
      setTimeout(setupObserver, 0);
    }
  };

  return {
    isStickyVisible,
    setContentFilterRef,
    setStickyHeaderRef
  };
};
